/**
 * shared/text/flow.ts — the one rule for "these two blocks are one sentence, hard-wrapped".
 *
 * ── Why this is in `shared/` and not beside either caller ────────────────────
 * It is asked by **two** subsystems that must agree or the product lies to the listener:
 *
 *   · `server/src/narration/plan.ts` uses it to pick the *pause* between two blocks — a 60 ms
 *     `seam` instead of a 620 ms `paragraph`, so the voice runs the halves of a sentence together.
 *   · `client/src/reader/Reader.tsx` uses it to pick the *layout* — whether the two blocks flow
 *     into one paragraph on the page.
 *
 * A second copy would drift on the first reworded condition, and the drift produces **no error
 * anywhere**: the page would show a paragraph the voice reads as two, or read as one what the page
 * breaks in half, and the read-along would look subtly wrong with nothing to point at. That is the
 * same failure `shared/text/segment.ts` opens by describing, one layer up. One rule, one file.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * ⚠️ **Out of the Dust, and the bug it names.** Karen Hesse's verse novel ships as one `<p>` per
 * verse line, so `ingest/blocks.ts` — correctly, faithfully — produces one `para` block per line:
 * 4026 blocks for 4087 sentences. Every layer downstream then treated each line as a whole
 * paragraph: the narration put 1.24 s of silence between "As summer wheat came ripe," and "so did
 * I,", and the page printed them as separate paragraphs. The book was tagged `prose`, was segmented
 * correctly, and nothing was misconfigured.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ── Why `para` only, and why that is the whole safety argument ───────────────
 * `verse` blocks are excluded and must stay excluded. Gitanjali and the Song Celestial are *real*
 * poetry: their lines also end without punctuation and continue in lower case, and there the line
 * break is the poet's — it must survive both on the page and in the pause. The parser already draws
 * exactly this distinction (`poetry` kind, or a `poem|verse|stanza|lyric|linegroup` class, produces
 * `verse` blocks; everything else produces `para`), so "a `para` that does not finish its sentence"
 * is precisely the set of hard-wrapped lines and nothing else. A book that genuinely wanted verse
 * treatment would not be in `para` blocks to begin with.
 *
 * Measured over the live library on 2026-08-13: this joins 1205 of Out of the Dust's 3887 block
 * boundaries and **exactly 0 in every other book** — Walden, Alice, Sherlock, Becoming, Sapiens, and
 * both poetry books included.
 *
 * ── The two conditions, and why neither alone is enough ─────────────────────
 * A missing terminator alone would join `Chapter One` to `It was a bright cold day`; a lower-case
 * opener alone would join `He stopped.` to `iPhone in hand`. Together they are the shape of a line
 * that was *wrapped* rather than *ended*.
 */

/**
 * Closing punctuation that can sit after a terminator without meaning the sentence continued.
 * The same set as `segment.ts`'s `CLOSERS`, for the same reason that file keeps exactly one.
 */
const TRAILING_CLOSERS = /["'”’»›)\]}]+$/u;

/** A terminator, or the punctuation that ends a clause hard enough to be a real break. */
const TERMINATES = /[.!?…:;]$/u;

/**
 * Does `prev` run straight into `next` — one grammatical sentence broken across two blocks?
 *
 * `prevText` is the tail the reader/voice actually reaches (the previous block's last sentence, or
 * simply its text) and `nextText` the head of what follows. Both callers pass whole-block text or
 * the adjacent sentence's text interchangeably: the last character of a block is the last character
 * of its final sentence, and the first is the first of its opening one.
 */
export function continuesSentence(prevKind: string, nextKind: string, prevText: string, nextText: string): boolean {
  if (prevKind !== "para" || nextKind !== "para") return false;
  const before = prevText.trimEnd().replace(TRAILING_CLOSERS, "");
  if (before === "" || TERMINATES.test(before)) return false;
  return /^\p{Ll}/u.test(nextText.trimStart());
}
